import { createEventListener } from '@solid-primitives/event-listener';
import { JSX, Accessor, createMemo, splitProps, createEffect, onCleanup } from 'solid-js';
import { createStore } from 'solid-js/store';
import { createMutationObserver } from '@solid-primitives/mutation-observer';
import { createResizeObserver } from '@solid-primitives/resize-observer';

import { cx } from '../utils';

/**
 * 这只是一个单纯的滚动条状态监听组件，可以做一些自定义的滚动样式，
 * 注：它并不直接提供交互，滚动交互是个比较复杂的问题，考虑到物理模拟，滚动性能，点击、拖动事件处理等，所以还是使用原生滚动。
 * @param props
 * @returns
 */
function ScrollbarBase(
  props: {
    scrollLeft: number;
    scrollWidth: number;
    clientWidth: number;
  } & Omit<JSX.HTMLAttributes<HTMLDivElement>, 'children'>
) {
  const [, others] = splitProps(props, ['class', 'scrollLeft', 'scrollWidth', 'clientWidth']);
  // const [total, setTotal] = createSignal(2);
  // const [size, setSize] = createSignal(1);
  // const [p, setP] = createSignal(0);
  const scrollSize = createMemo(() => props.scrollWidth - props.clientWidth);
  const percent = () => props.scrollLeft / scrollSize();
  const w = () => props.clientWidth / props.scrollWidth;
  const t = () => {
    const w1 = w();
    return (percent() * (1 - w1)) / w1;
  };

  return (
    <div {...others} class={cx('h-1 rounded-sm bg-layer4 overflow-hidden', props.class)}>
      <div
        class="bg-layer6 rounded h-full"
        style={{
          transform: `translateX(${100 * t()}%)`,
          width: `${100 * w()}%`,
        }}
      ></div>
    </div>
  );
}

function createScrollProps(el: Accessor<Element>) {
  const [props, setProps] = createStore({
    isScrolling: false,
    scrollLeft: 0,
    scrollTop: 0,
    scrollWidth: 1,
    scrollHeight: 1,
    clientWidth: 0,
    clientHeight: 0,
  });

  // 有时候会有一个问题就是scrollWidth变化了，但是由于clientWidth没有变化，不会触发ResizeObserver，我们拿不到正确的scrollWidth，这里通过MutationObserver进行监控
  const [observer, { stop }] = createMutationObserver(
    [],
    {
      childList: true,
    },
    () => {
      setProps({
        scrollWidth: el().scrollWidth,
        scrollHeight: el().scrollHeight,
        clientWidth: el().clientWidth,
        clientHeight: el().clientHeight,
      });
    }
  );

  createResizeObserver(el, () => {
    setProps({
      scrollWidth: el().scrollWidth,
      scrollHeight: el().scrollHeight,
      clientWidth: el().clientWidth,
      clientHeight: el().clientHeight,
    });
  });

  createEffect(() => {
    if (el()) {
      observer(el());
      onCleanup(() => {
        stop();
      });
    }
  });

  let scrollTimeout: ReturnType<typeof setTimeout> | undefined;
  createEventListener(el, 'scroll', (e: any) => {
    if (scrollTimeout !== undefined) clearTimeout(scrollTimeout);

    /** Safari 不支持ScrollEnd事件 通过setTimeout判断*/
    scrollTimeout = setTimeout(() => {
      setProps('isScrolling', false);
    }, 60);
    setProps('isScrolling', true);
    setProps('scrollLeft', e.target.scrollLeft);
    setProps('scrollTop', e.target.scrollTop);
  });

  /** Safari 不支持该事件 */
  createEventListener(el, 'scrollend', () => {
    setProps('isScrolling', false);
  });

  return props;
}

const Scrollbar = ScrollbarBase as typeof ScrollbarBase & {
  createScrollProps: typeof createScrollProps;
};
Scrollbar.createScrollProps = createScrollProps;

export default Scrollbar;
